import * as React from 'react';
import {
  Alert,
  Avatar,
  ConfirmDialog,
  MobileHeader,
  MobileScreen,
  SettingsList,
  SettingsRow,
} from '@relay-towing/ui';
import {
  Bell,
  Camera,
  HelpCircle,
  LayoutDashboard,
  LogOut,
  Mail,
  MapPin,
  Shield,
  Trash2,
} from 'lucide-react';
import { deleteRefusal, useDeleteAccount } from '../../hooks/useDeleteAccount';
import {
  useDevicePermissions,
  type DevicePermission,
} from '../../hooks/useDevicePermissions';
import { useDriverProfile } from '../../hooks/useDriverProfile';
import { useHybridAuth } from '../../hooks/useHybridAuth';
import { useMember } from '../../hooks/useMember';
import { useNotificationSetting } from '../../hooks/useNotificationSetting';
import { links } from '../../lib/links';
import {
  isNative,
  native,
  openAppSettings,
  openExternal,
} from '../../lib/nativeBridge';
import { DutyStatusRow } from '../shell/DutyStatusRow';
import { useBack } from '../shell/useBack';
import { PlanSection } from './PlanSection';
import { ProfileSheet } from './ProfileSheet';

const PERMISSION_WORDS: Record<DevicePermission, string> = {
  granted: 'Allowed',
  denied: 'Not allowed',
  prompt: 'Asks first',
  unknown: 'Set in the phone',
};

/**
 * Every setting, one web screen in every wrapper and browser: it replaces
 * the old web overlay and the native settings screens (docs/native-bridge.md
 * says the v2 wrappers removed theirs). The hidden stubs the old screen had
 * (personal info, payment, schedule, auto-accept, dark mode, app icon) are
 * gone, not rebuilt.
 */
export function SettingsScreen() {
  const back = useBack('/');
  const { user, logout } = useHybridAuth();
  const { data: profile } = useDriverProfile();
  const member = useMember();
  const notifications = useNotificationSetting();
  const device = useDevicePermissions();
  const deleteAccount = useDeleteAccount();
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const [editingProfile, setEditingProfile] = React.useState(false);
  const inWrapper = isNative();

  const name = profile?.name?.trim() || user?.name || 'Me';
  const email = user?.email || profile?.email;

  const signOut = () => logout();

  return (
    <MobileScreen
      header={<MobileHeader onBack={back} backLabel="Back" title="Settings" />}
    >
      <SettingsList title="Profile">
        <SettingsRow
          label={name}
          description={email}
          control={<Avatar name={name} src={user?.picture} size="sm" />}
          onClick={() => setEditingProfile(true)}
        />
      </SettingsList>
      <ProfileSheet
        open={editingProfile}
        onOpenChange={setEditingProfile}
        // The name the screen shows. The server writes the new one to the
        // account and every driver record, so the two match once saved.
        name={profile?.name?.trim() || user?.name || ''}
        email={email}
      />

      {member.drives && member.can('drivers.edit_status') ? (
        <SettingsList title="Status">
          <DutyStatusRow />
        </SettingsList>
      ) : null}

      <PlanSection />

      {notifications.supported ? (
        <SettingsList
          title="Notifications"
          footnote={
            notifications.blocked
              ? 'Notifications are turned off for Relay in this device’s settings.'
              : undefined
          }
        >
          <SettingsRow
            label="Push notifications"
            icon={Bell}
            toggle={{
              checked: notifications.on,
              onCheckedChange: notifications.setOn,
              disabled: notifications.busy,
            }}
          />
          <SettingsRow
            label="Send a test notification"
            disabled={!notifications.on}
            onClick={notifications.sendTest}
          />
        </SettingsList>
      ) : null}

      <SettingsList
        title="Device"
        footnote={
          !inWrapper &&
          (device.camera === 'denied' || device.location === 'denied')
            ? 'Turned off for Relay in this browser. Allow it from the site settings beside the address bar.'
            : undefined
        }
      >
        {/* In a wrapper the phone's settings say what Relay may use, so each
            row opens them; in a browser a row asks, as the first photo or
            the first fix would. */}
        <SettingsRow
          label="Camera"
          icon={Camera}
          value={PERMISSION_WORDS[device.camera]}
          onClick={
            inWrapper
              ? () => openAppSettings()
              : device.camera === 'granted' || device.camera === 'denied'
                ? undefined
                : () => device.ask('camera')
          }
        />
        <SettingsRow
          label="Location"
          icon={MapPin}
          value={
            inWrapper
              ? 'Reported by the app'
              : PERMISSION_WORDS[device.location]
          }
          onClick={
            inWrapper
              ? () => openAppSettings()
              : device.location === 'granted' || device.location === 'denied'
                ? undefined
                : () => device.ask('location')
          }
        />
      </SettingsList>

      {member.officeRole ? (
        <SettingsList title="Office">
          <SettingsRow
            label="Go to dashboard"
            icon={LayoutDashboard}
            external
            onClick={() => openExternal(links.dashboard)}
          />
        </SettingsList>
      ) : null}

      <SettingsList title="About">
        <SettingsRow
          label="Terms and privacy"
          icon={Shield}
          external
          onClick={() => openExternal(links.legal)}
        />
        <SettingsRow
          label="Help center"
          icon={HelpCircle}
          external
          onClick={() => openExternal(links.helpCenter)}
        />
        <SettingsRow
          label="Email support"
          icon={Mail}
          external
          onClick={() => openExternal(links.support)}
        />
        <SettingsRow label="Version" value={native()?.appVersion ?? 'Web'} />
      </SettingsList>

      <SettingsList title="Account">
        <SettingsRow
          label="Sign out"
          icon={LogOut}
          destructive
          onClick={signOut}
        />
        <SettingsRow
          label="Delete account"
          icon={Trash2}
          destructive
          onClick={() => setConfirmingDelete(true)}
        />
      </SettingsList>

      {deleteAccount.isError ? (
        <Alert tone="destructive" title="Your account was not deleted">
          {deleteRefusal(deleteAccount.error)}
        </Alert>
      ) : null}

      <ConfirmDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        title="Delete your account?"
        description="Your login and your place in every company go. Past calls and invoices stay, with your name taken off. This cannot be undone."
        confirmLabel="Delete account"
        destructive
        loading={deleteAccount.isPending}
        onConfirm={() =>
          deleteAccount.mutate(undefined, {
            onSettled: () => setConfirmingDelete(false),
          })
        }
      />
    </MobileScreen>
  );
}
